import { useFetch } from '@chipinside/fetcher'
import { LoadingIconButton } from '@chipinside/frontend'
import { Translate } from '@chipinside/translate'
import { Badge, Box } from '@mui/material'
import Snackbar from '@mui/material/Snackbar'
import { styled } from '@mui/material/styles'
import { isEmpty } from 'lodash'
import React, { useEffect, useState } from 'react'

import DrawerBox from '#/components/DrawerBox'
import { Icon } from '#/components/Icon'
import { useFarmSlug } from '#/store/ducks/farm'
import { endpoint } from '#/store/services/endpoints'

import TabPanel from './Manipulate/TabPanel'
import { TabsButtons } from './Manipulate/TabsButtons'
import ViewAll from './Manipulate/ViewAll'
import AnimalSystemTab from './Tabs/AnimalSystem'
import JobsTab from './Tabs/Jobs'
import ReleaseTab from './Tabs/Release'

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    color: '#fff',
    backgroundColor: theme.palette.primary.main,
    fontSize: 12,
  },
}))

const REFRESH_INTERVAL = 1000 * 60 * 10 // 10 minutes

export default function Notifications({ context }) {
  const farmSlug = useFarmSlug()
  const [isOpen, setIsOpen] = useState(false)
  const [value, setValue] = useState(0)
  const [hasNewRelease, setHasNewRelease] = useState(false)
  const [alerts, setAlerts] = useState(null)
  /** Snackbar Alert */
  const [showSnack, setShowSnack] = useState(false)
  const [newMessage, setNewMessage] = useState(null)

  const controller =
    context === 'farm'
      ? endpoint.notifications.farm({ farm: farmSlug })
      : endpoint.notifications.system()

  const {
    data: headerData,
    mutate,
    isValidating,
  } = useFetch({
    controller,
    swrOptions: { refreshInterval: REFRESH_INTERVAL },
  })

  const handleTabSwitch = (_, tabNumber) => {
    /** Set Header if cache clear */

    setIsOpen(true)
    setValue(tabNumber)
  }

  const [tabController, setTabController] = useState()
  const [tabFilters, setTabFilters] = useState()

  const {
    data: tabData,
    mutate: mutateTabData,
    isValidating: isLoadingTab,
  } = useFetch({
    controller: isOpen && tabController,
    params: { filters: tabFilters },
    swrOptions: {
      refreshInterval: REFRESH_INTERVAL,
      revalidateOnMount: true,
      revalidateIfStale: false,
    },
  })

  const ReleaseNote = ReleaseTab({
    ...alerts?.release,
    farm: farmSlug,
    handleTabSwitch,
    setHasNewRelease,
    setIsOpen,
    context,
    viewed_last_release_note: headerData?.viewed_last_release_note,
    mutate: () => {
      mutate()
      mutateTabData()
    }, // BELL
    isLoading: isLoadingTab,
  })
  const Jobs = JobsTab({
    ...alerts?.jobs,
    farm: farmSlug,
    handleTabSwitch,
    isOpen,
    setIsOpen,
    setAlerts,
    context,
    isLoading: isLoadingTab,
  })
  const Animal = () =>
    AnimalSystemTab({
      ...alerts?.animal,
      farm: farmSlug,
      setIsOpen,
      type: 'animal',
      setShowSnack,
      setNewMessage,
      isLoading: isLoadingTab,
    })
  const System = () =>
    AnimalSystemTab({
      ...alerts?.system,
      farm: farmSlug,
      setIsOpen,
      type: 'system',
      setShowSnack,
      setNewMessage,
      isLoading: isLoadingTab,
    })

  /**
   * Tab List/Content
   * */
  const Tabs = context === 'farm' ? [Animal(), System(), Jobs, ReleaseNote] : [Jobs, ReleaseNote]

  useEffect(() => {
    const { endpoint, filters } = Tabs[value]

    setTabController(endpoint)
    setTabFilters(filters)
  }, [value])

  useEffect(() => {
    const { key } = Tabs[value]
    const timestamps = tabData?.data?.map(({ timestamp }) => new Date(timestamp))
    const timestamp = !isEmpty(timestamps) ? Math.max(...timestamps) : undefined

    let alerts = {
      [key]: { data: tabData, status: 'done', timestamp },
    }

    if (key !== 'release') {
      alerts[key].data = tabData?.data
    }

    setAlerts(prevState => (prevState ? { ...prevState, ...alerts } : alerts))
  }, [tabData])

  /**
   * Tab check, open tab with new/unread alert
   */
  useEffect(() => {
    if (!isOpen) {
      if (headerData) {
        /**TAB ANIMALS - INDEX 0 */
        if (headerData.unread_animal_alerts > 0) {
          setValue(0)
          /**TAB SYSTEM - INDEX 1 */
        } else if (headerData.unread_system_alerts > 0) {
          setValue(1)
          /**TAB JOBS - INDEX 2 || 0*/
        } else if (headerData.jobs > 0) {
          setValue(context === 'farm' ? 2 : 0)
        }
        /** DEFAULT */
      } else {
        setValue(0)
      }
    }
  }, [headerData])
  /*
   * onClose conditional, if is ReleaseNote Tab (value === 3)
   * simulates clicking on "Ver Depois", avoids reopening the tab with each click/window
   */
  return (
    <>
      <LoadingIconButton
        id="notifications"
        loading={isValidating}
        onClick={() => handleTabSwitch(null, value)}
        size="large"
        icon={
          <StyledBadge badgeContent={headerData?.bell}>
            <Icon name="notification" size={20} />
          </StyledBadge>
        }
      />
      <DrawerBox
        pageName={<h2 style={{ margin: 0 }}>{Translate({ messageKey: 'notification_plural' })}</h2>}
        isOpen={isOpen}
        onClose={() =>
          value === 3 ? ReleaseNote.onClose(alerts?.release?.data?.id) : setIsOpen(false)
        }
      >
        <TabsButtons
          data={headerData}
          value={value}
          handleTabSwitch={handleTabSwitch}
          mutate={mutateTabData}
          hasNewRelease={hasNewRelease}
          context={context}
        />
        {Tabs.map((tab, index) => (
          <TabPanel {...tab} value={value} index={index} setIsOpen={setIsOpen} key={tab.key}>
            {tab.timestamp && (tab.key === 'system' || tab.key === 'animal') ? (
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginBottom: 10,
                  marginRight: 10,
                }}
                key={`view-all-${tab.key}`}
              >
                <ViewAll
                  timestamp={tab.timestamp}
                  filters={[{ name: 'alert_type', value: tab.key }]}
                  enabled={headerData?.[`unread_${tab.key}_alerts`] > 0}
                  refresh={() => mutate()}
                />
              </Box>
            ) : null}
            {tab.content}
          </TabPanel>
        ))}
      </DrawerBox>
      {/*SNACK BAR ON NEW ALERT*/}
      <Snackbar
        open={showSnack}
        onClose={() => setShowSnack(false)}
        autoHideDuration={5000}
        message={newMessage}
      />
    </>
  )
}
